import React from "react"
import { Badge } from "reactstrap"
import moment from "moment"
import { Link } from "react-router-dom"
import { childNumber } from "pages/Tenis10Claims/claimsLabels"

const orderTypeMeta = {
  bookingRefund: {
    amountLabel: "Returned to this player",
    label: "Booking refund",
  },
  courtBooking: {
    amountLabel: "Charged to this player",
    label: "Court booking",
  },
  eventRefund: {
    amountLabel: "Returned to this player",
    label: "Tournament refund",
  },
  eventRegistration: {
    amountLabel: "Charged to this player",
    label: "Tournament registration",
  },
  eventWithdrawal: {
    amountLabel: "Returned to this player",
    label: "Tournament withdrawal",
  },
  firstTaskCompletion: {
    amountLabel: "Reward credited to this player",
    label: "First-task reward",
  },
  premiumRefund: {
    amountLabel: "Returned to this player",
    label: "Membership refund",
  },
  referralReward: {
    amountLabel: "Reward credited to this player",
    label: "Referral reward",
  },
  subtractByAdmin: {
    amountLabel: "Removed by an administrator",
    label: "Wallet deduction",
  },
  topUp: {
    amountLabel: "Added to this player's wallet",
    label: "Wallet top-up",
  },
  topUpByAdmin: {
    amountLabel: "Added by an administrator",
    label: "Wallet top-up",
  },
  upgradeMembership: {
    amountLabel: "Charged to this player",
    label: "Membership purchase",
  },
  upgradeMembershipByAdmin: {
    amountLabel: "Applied by an administrator",
    label: "Membership update",
  },
  waitingListRefund: {
    amountLabel: "Returned to this player",
    label: "Waiting-list refund",
  },
}

const statusMeta = {
  confirmed: { color: "success", label: "Confirmed" },
  pending: { color: "warning", label: "Pending" },
  refunded: { color: "info", label: "Refunded" },
  rejected: { color: "danger", label: "Rejected" },
}

const humanizeValue = value => {
  if (!value) return "Not recorded"

  const words = String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()

  return words.replace(/^./, character => character.toUpperCase())
}

const formatPerson = person => {
  const name = [person?.firstName, person?.lastName].filter(Boolean).join(" ")
  const id = person?.userId

  if (name && id) return `${name} (#${id})`
  if (name) return name
  if (id) return `Player #${id}`
  return "Partner details unavailable"
}

export const getOrderPresentation = order => ({
  ...orderTypeMeta[order?.type],
  amountLabel:
    orderTypeMeta[order?.type]?.amountLabel || "Recorded for this player",
  label: orderTypeMeta[order?.type]?.label || humanizeValue(order?.type),
  status:
    statusMeta[String(order?.status || "").toLowerCase()] ||
    { color: "secondary", label: humanizeValue(order?.status) },
})

const formatTransaction = (cellContent, order) => {
  const presentation = getOrderPresentation(order)
  const date = order?.createdAt ? moment(order.createdAt) : null

  return (
    <div style={{ minWidth: "205px" }}>
      <div className="d-flex align-items-center flex-nowrap">
        <span className="font-weight-semibold mr-2 text-nowrap">
          Payment #{order?.paymentId || "—"}
        </span>
        <Badge
          color={presentation.status.color}
          pill
          className="px-2 py-1 font-size-11 flex-shrink-0"
        >
          {presentation.status.label}
        </Badge>
      </div>
      <div className="text-muted font-size-11 mt-1">
        {date?.isValid()
          ? date.format("D MMM YYYY, HH:mm")
          : "Date not recorded"}
      </div>
    </div>
  )
}

// A Tenis10 order is this (the parent's) account's, for a child: the row's `profileId`, or a card passport's product's — the same
// rule as the admin Payments read. The child is named from the account's children, with its own number, as its entry log row is.
const orderProfileId = order =>
  order?.profileId ||
  (order?.type === "tenis10Passport" ? order?.products?.[0]?.id : null)

const OrderChild = ({ profileId, accountChildren }) => {
  const child = accountChildren.find(item => item?.profileId === profileId)
  const name = [child?.firstName, child?.lastName].filter(Boolean).join(" ")
  const number = childNumber(child)
  const label = [name, number != null ? `#${number}` : null]
    .filter(Boolean)
    .join(" ")

  return (
    <div className="text-muted font-size-12 mt-1">
      {label ? `Tenis10 child: ${label}` : "For a Tenis10 child"}
    </div>
  )
}

const formatPurpose = (cellContent, order, accountChildren = []) => {
  const presentation = getOrderPresentation(order)
  const productName = order?.products?.find(product => product?.name)?.name
  const eventLabel = order?.eventName || productName
  const profileId = orderProfileId(order)

  return (
    <div style={{ minWidth: "220px", whiteSpace: "normal" }}>
      <div className="font-weight-semibold">{presentation.label}</div>
      {order?.eventId && eventLabel ? (
        <Link
          className="d-block mt-1"
          target="_blank"
          to={`/events/${order.eventId}/details`}
        >
          {eventLabel}
        </Link>
      ) : productName ? (
        <div className="text-muted font-size-12 mt-1">{productName}</div>
      ) : order?.notes ? (
        <div className="text-muted font-size-12 mt-1">{order.notes}</div>
      ) : (
        <div className="text-muted font-size-12 mt-1">
          No related item recorded
        </div>
      )}
      {order?.eventId && order?.competitionId && (
        <Link
          className="d-inline-block font-size-12 mt-1"
          target="_blank"
          to={`/events/${order.eventId}/competitions/${order.competitionId}/entries`}
        >
          Open competition
        </Link>
      )}
      {profileId && (
        <OrderChild profileId={profileId} accountChildren={accountChildren} />
      )}
    </div>
  )
}

const formatAmount = (cellContent, order) => {
  const presentation = getOrderPresentation(order)
  const coversPartner = Boolean(order?.partner?.userId)
  const isPlayerTotal = ["courtBooking", "eventRegistration"].includes(
    order?.type
  )

  return (
    <div style={{ minWidth: "175px", whiteSpace: "normal" }}>
      <div className="font-weight-semibold">
        {order?.amount ?? "—"} {order?.currency || ""}
      </div>
      <div className="text-muted font-size-11 mt-1">
        {coversPartner
          ? "Team total · 2 players"
          : isPlayerTotal
          ? "Player total"
          : "Transaction total"}
      </div>
      <div className="font-size-12 mt-1">{presentation.amountLabel}</div>
      {coversPartner && (
        <div className="text-muted font-size-11 mt-1">
          Covers {formatPerson(order.partner)}
        </div>
      )}
    </div>
  )
}

const formatSource = (cellContent, order, showBalance) => {
  if (showBalance) {
    return (
      <div style={{ minWidth: "120px" }}>
        <div className="text-muted font-size-11">Balance after</div>
        <div className="font-weight-semibold mt-1">
          {order?.user?.walletBalance ?? "Not recorded"}
          {order?.currency ? ` ${order.currency}` : ""}
        </div>
      </div>
    )
  }

  const adminName = formatPerson(order?.admin)

  return (
    <div style={{ minWidth: "165px", whiteSpace: "normal" }}>
      <div className="font-weight-semibold">
        {humanizeValue(order?.paymentType)}
      </div>
      <div className="text-muted font-size-12 mt-1">
        {order?.admin?.id ? (
          <Link to={`/accounts/admins/${order.admin.id}`}>
            Administrator: {adminName}
          </Link>
        ) : order?.admin?.userId ? (
          `Administrator: ${adminName}`
        ) : (
          "Player initiated"
        )}
      </div>
      {order?.notes && order?.eventId && (
        <div className="text-muted font-size-11 mt-1">{order.notes}</div>
      )}
    </div>
  )
}

const orderColumns = ({ showBalance = false, accountChildren = [] } = {}) => [
  {
    dataField: "paymentId",
    formatter: formatTransaction,
    headerStyle: { width: "20%" },
    text: "Transaction",
  },
  {
    dataField: "type",
    formatter: (cellContent, order) =>
      formatPurpose(cellContent, order, accountChildren),
    headerStyle: { width: "32%" },
    text: "Purpose",
  },
  {
    dataField: "amount",
    formatter: formatAmount,
    headerStyle: { width: "24%" },
    text: "Amount",
  },
  {
    dataField: showBalance ? "balance" : "paymentType",
    formatter: (cellContent, order) =>
      formatSource(cellContent, order, showBalance),
    headerStyle: { width: "24%" },
    text: showBalance ? "Wallet balance" : "Payment source",
  },
]

export default orderColumns
